import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FolderOpen, Trash2, Eye } from 'lucide-react';
import type { ProjetoSalvo, ConfiguracaoChapa } from '../../types/corte';
import {
  calcularGruposFromPecas,
  calcularPecasAcabadasFromGrupos,
} from '../../store/corte/useCorteStore';
import VisualizacaoCorte from './VisualizacaoCorte';

const HOVER_PREVIEW_DELAY_MS = 120;
const HOVER_PREVIEW_HIDE_DELAY_MS = 280;
const PREVIEW_LARGURA = 420;

function configuracaoPadrao(cfg?: ConfiguracaoChapa): ConfiguracaoChapa {
  return {
    larguraTotal: Number(cfg?.larguraTotal) || 2200,
    comprimentoTotal: Number(cfg?.comprimentoTotal) || 2750,
    espessuraSerra: Number(cfg?.espessuraSerra) || 4,
  };
}

interface CatalogoProjetoCardProps {
  projeto: ProjetoSalvo;
  onCarregar: () => void;
  onRemover: () => void;
}

export default function CatalogoProjetoCard({
  projeto,
  onCarregar,
  onRemover,
}: CatalogoProjetoCardProps) {
  const [mostrarPreview, setMostrarPreview] = useState(false);
  const [posPreview, setPosPreview] = useState<{ top: number; left: number } | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const configuracao = useMemo(() => configuracaoPadrao(projeto.configuracao), [projeto.configuracao]);

  const grupos = useMemo(
    () => calcularGruposFromPecas(projeto.pecas ?? [], configuracao),
    [projeto.pecas, configuracao]
  );
  const pecasAcabadas = useMemo(() => calcularPecasAcabadasFromGrupos(grupos), [grupos]);

  const atualizarPosicao = useCallback(() => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margem = 12;
    let left = r.right + 4;
    if (left + PREVIEW_LARGURA > window.innerWidth - margem) {
      left = Math.max(margem, r.left - PREVIEW_LARGURA - 4);
    }
    let top = r.top;
    const alturaMax = Math.min(380, window.innerHeight - margem * 2);
    if (top + alturaMax > window.innerHeight - margem) {
      top = Math.max(margem, window.innerHeight - alturaMax - margem);
    }
    setPosPreview({ top, left });
  }, []);

  const cancelarOcultar = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
  };

  const iniciarPreview = () => {
    cancelarOcultar();
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      atualizarPosicao();
      setMostrarPreview(true);
    }, HOVER_PREVIEW_DELAY_MS);
  };

  const agendarOcultar = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = null;
    cancelarOcultar();
    hideTimerRef.current = setTimeout(() => {
      setMostrarPreview(false);
      setPosPreview(null);
    }, HOVER_PREVIEW_HIDE_DELAY_MS);
  };

  useEffect(() => {
    if (!mostrarPreview) return;
    const onReposicionar = () => atualizarPosicao();
    window.addEventListener('scroll', onReposicionar, true);
    window.addEventListener('resize', onReposicionar);
    return () => {
      window.removeEventListener('scroll', onReposicionar, true);
      window.removeEventListener('resize', onReposicionar);
    };
  }, [mostrarPreview, atualizarPosicao]);

  useEffect(
    () => () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    },
    []
  );

  const previewPortal =
    mostrarPreview &&
    posPreview &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        className="fixed z-[9999] rounded-xl border-2 border-[#009b3a]/50 bg-[#fafbfc] p-3 shadow-2xl"
        style={{
          top: posPreview.top,
          left: posPreview.left,
          width: PREVIEW_LARGURA,
          maxHeight: 'min(420px, calc(100vh - 24px))',
        }}
        onMouseEnter={() => {
          cancelarOcultar();
          setMostrarPreview(true);
        }}
        onMouseLeave={agendarOcultar}
        role="tooltip"
      >
        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-black">
          <Eye className="h-3.5 w-3.5 shrink-0 text-[#009b3a]" />
          <span className="truncate">
            {projeto.nomeProduto}
            {projeto.nomePeca ? ` · ${projeto.nomePeca}` : ''}
          </span>
        </p>
        <div className="catalogo-preview-scroll max-h-[min(360px,calc(100vh-80px))] rounded-lg border border-[#009b3a]/20 bg-[#eef0f4]">
          {(projeto.pecas?.length ?? 0) > 0 && grupos.some((g) => g.pecas.length > 0) ? (
            <div className="inline-block w-max min-w-full p-1">
              <VisualizacaoCorte
                grupos={grupos}
                configuracao={configuracao}
                projeto={{
                  nomeProduto: projeto.nomeProduto,
                  nomePeca: projeto.nomePeca,
                }}
                rotacao={0}
                pecasAcabadas={pecasAcabadas}
                modoCompacto
                mostrarResumo={false}
              />
            </div>
          ) : (
            <p className="p-6 text-center text-sm text-neutral-600">
              Este projeto não tem peças de corte para desenhar.
            </p>
          )}
        </div>
      </div>,
      document.body
    );

  return (
    <>
      {previewPortal}
      <div
        ref={cardRef}
        className="relative rounded-lg border border-[#009b3a]/15 bg-[#fafbfc] p-4 transition-all hover:border-[#009b3a]/35 hover:shadow-md"
        onMouseEnter={iniciarPreview}
        onMouseLeave={agendarOcultar}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-bold text-black">{projeto.nomeProduto}</p>
            {projeto.nomePeca && (
              <p className="truncate text-sm text-neutral-600">{projeto.nomePeca}</p>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemover();
            }}
            className="shrink-0 text-red-500 hover:text-red-700"
            title="Excluir projeto"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-1 text-xs text-neutral-600">
          {projeto.dataCriacao} · {projeto.pecas?.length ?? 0} cortes
        </p>
        <p className="mb-2 text-[10px] font-medium text-[#009b3a]">Passe o mouse para ver o desenho</p>
        <button
          type="button"
          onClick={onCarregar}
          className="ade-btn-gold relative z-10 flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm"
        >
          <FolderOpen className="h-4 w-4" />
          Carregar
        </button>
      </div>
    </>
  );
}
